/**
 * Ported VERBATIM from `apr_template/src/pages/FrameworkBuilder.tsx`.
 *
 * Only import paths change. The markup, class names, copy, state and logic are the
 * source's — the stylesheet is copied alongside, and the global classes it also uses
 * resolve because the screen renders inside `AprScreen` (`.apr-app`).
 *
 * Do not "tidy" anything here. A difference is a difference from the source.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authFetch } from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './FrameworkBuilder.css';
import {
  ASPECTS,
  ASPECT_COLOR,
  ASPECT_HINT,
  ASPECT_OWNER,
  effectiveAspect,
  type Aspect,
} from '../aspects';

const FrameworkBuilderHelp: Help = {
  title: 'Framework Builder',
  icon: '🏗️',
  purpose:
    'A <strong>framework</strong> is the scoring model used to assess every application. It is a tree: a framework holds <strong>dimensions</strong>, each dimension holds <strong>components</strong>, and each component holds <strong>questions</strong> whose <strong>answers</strong> are pre-scored. Building that tree here defines exactly how applications are scored and, ultimately, which 4R recommendation they receive.',
  doHere: [
    'Choose a <strong>Framework</strong> from the selector above the tree. Nothing is selected by default, and the tree shows that framework’s dimensions and components — not the whole library.',
    'Add a <strong>Dimension</strong> — a broad area of assessment (e.g. Business Value, Security Posture) — and say <strong>who answers it</strong>: Technical, Operational, Business, Security or Strategy. The aspect is required, because it decides who receives those questions.',
    'Inside a dimension, add <strong>Components</strong> — the specific topics that make up that dimension.',
    'Set each component’s <strong>weight</strong> by clicking its row of <strong>dots</strong> — this is how you make a component count more or less (see below).',
    'Add <strong>Questions</strong> to a component, then give each question its <strong>answer options</strong>, scoring every option from <strong>1 to 5</strong>.',
    'Change a dimension’s aspect with the <strong>coloured selector</strong> on its row. That is the dimension’s default, everywhere it is used — to make one framework differ, set an override on its column in <strong>Framework Designer</strong>.',
    'To move a component to a different dimension, use <strong>Framework Designer</strong> — its board is where placement is arranged, and a move there applies to that framework only.',
    'To reuse a whole framework, use <strong>Clone</strong> on the <strong>Frameworks</strong> screen. It copies the framework with its dimensions, its component placement and any aspect overrides. The dimensions themselves are <em>shared</em>, not copied — editing one in the clone edits it in the original too, which is what the per-framework override exists to avoid.',
    'Edits save as you go. A framework already in use by an APR run is <strong>locked</strong> from structural change so existing scores stay stable.',
  ],
  fits: 'Framework &rarr; Framework Builder',
  firstTime:
    'Pick a framework, add a dimension and say who answers it, add a component inside it, set the component’s weight with its dots, then add a question with a few answer options scored 1–5.',
  sections: [
    {
      type: 'html',
      label: 'The building blocks',
      html:
        '<ul style="margin:0;padding-left:18px;line-height:1.6">' +
        '<li><strong>Framework</strong> — the complete scoring model applied to a portfolio. One framework can be reused across many APR runs.</li>' +
        '<li><strong>Dimension</strong> — a broad area the framework assesses (e.g. Business Value, Security Posture, Risk). Each dimension names <strong>who answers it</strong> — Technical (the application owner), Operational (the technical custodian), Business (the business process owner), Security, or Strategy (the executive owner) — and the questionnaire is split along those lines, so each owner is only asked their own part.</li>' +
        '<li><strong>Component</strong> — a specific topic within a dimension (e.g. “Vendor viability” under Risk). A component carries a <strong>weight</strong>.</li>' +
        '<li><strong>Question</strong> — a question asked during the assessment, belonging to a component.</li>' +
        '<li><strong>Answer option</strong> — one of the possible answers to a question, each carrying a fixed <strong>score</strong> from 1 to 5.</li>' +
        '</ul>',
    },
    {
      type: 'defs',
      label: 'Who answers a dimension',
      items: [
        { term: 'Technical', color: '#1565c0', def: 'The <strong>Application Owner</strong> — the architect; accountable for the application as a system.' },
        { term: 'Operational', color: '#00695c', def: 'The <strong>Technical Owner / Custodian</strong> — run and maintain: platform, patching, availability.' },
        { term: 'Business', color: '#6a1b9a', def: 'The <strong>Business Process Owner</strong> — owns the process the application serves.' },
        { term: 'Security', color: '#c62828', def: 'The <strong>Security Owner</strong> — security posture, controls and compliance.' },
        { term: 'Strategy', color: '#e65100', def: 'The <strong>Executive Owner</strong>, also called the sponsor — the application’s future and its funding.' },
      ],
      note: 'Each of these is a person named on the application in <strong>Application Master Data</strong>. When a questionnaire is sent, it is split by aspect and each owner receives only their own questions — and nothing is sent at all until every owner a framework asks for is filled in on every application.',
    },
    {
      type: 'html',
      label: 'Deleting takes everything underneath',
      html:
        'Deleting a <strong>dimension</strong> also deletes its components, their questions and every answer option — in every framework that used it. The same is true of deleting a component, which takes its questions and answers. There is no undo. If you only want a dimension out of <em>one</em> framework, remove it from that framework in <strong>Framework Designer</strong> instead; the dimension and its content stay in the library.',
    },
    {
      type: 'defs',
      label: 'Answer score — how much value an answer represents (1–5)',
      items: [
        { term: '1', color: '#7f1d1d', def: '<strong>Minimum value</strong> — the weakest / least favourable answer.' },
        { term: '2', color: '#9a3412', def: 'Below average.' },
        { term: '3', color: '#9a3412', def: 'Average / neutral.' },
        { term: '4', color: '#14532d', def: 'Above average.' },
        { term: '5', color: '#14532d', def: '<strong>Maximum value</strong> — the strongest / most favourable answer.' },
      ],
      note: 'Every answer option is scored 1–5. When an application is assessed, the option chosen for each question contributes its score.',
    },
    {
      type: 'defs',
      label: 'Component weight — how important the component is (1–5)',
      items: [
        { term: '1', color: '#7f1d1d', def: '<strong>Not important</strong> — counts least toward the dimension’s score.' },
        { term: '2', color: '#9a3412', def: 'Low importance.' },
        { term: '3', color: '#9a3412', def: 'Default / balanced importance.' },
        { term: '4', color: '#14532d', def: 'High importance.' },
        { term: '5', color: '#14532d', def: '<strong>Critical</strong> — counts most toward the dimension’s score.' },
      ],
      note: 'Set a component’s weight with the row of <strong>dots</strong> beside it: click the <strong>Nth dot to set the weight to N</strong>. The filled dots show the current weight. A heavier component pulls its dimension’s score more strongly than a lighter one.',
    },
    {
      type: 'html',
      label: 'How the score rolls up',
      html:
        'Scores climb the tree to a single result: the <strong>answer</strong> chosen gives a question its 1–5 score → a <strong>component’s</strong> score is the average of its questions → a <strong>dimension’s</strong> score is the <strong>weighted</strong> average of its components (using the component weights) → the <strong>application’s</strong> score is the average of its dimensions, on a <strong>1.00–5.00</strong> scale. That final score drives the 4R recommendation.<br><br>Component and dimension scores appear as each owner finishes their part, but the <strong>application score and its 4R recommendation wait for the last question across every aspect</strong> — a portfolio score built from a half-answered assessment would not be comparable with a complete one.',
    },
  ],
};

/* ============================================================================
 * Framework Builder — single-screen editor for the whole framework library.
 * Edits the GLOBAL tree: dimension -> component -> question -> answer.
 * Every mutation auto-saves (optimistic) via authFetch; no full reloads.
 * ==========================================================================*/

/**
 * The dimension's responsible aspect — who answers it.
 *
 * This was `'Business' | 'Technology'`, a descriptive label with no consumer beyond the chip
 * and the filter on this screen. Owner-directed 2026-08-25, the same field now carries the
 * five owner types, and it is load-bearing: it decides which of an application's five owners
 * receives this dimension's questions. Migration 0083 re-tagged every existing row to
 * 'Technical', which routes to the application owner exactly as before.
 */
type Category = Aspect;

interface Answer {
  id: string;
  text: string;
  score: number; // 1..5, unique per question
}

interface Question {
  id: string;
  componentId: string;
  text: string;
  options: Answer[];
}

interface Component {
  id: string;
  dimensionId: string;
  name: string;
  description: string;
  displayOrder: number;
  weight: number; // 1..5
  questions: Question[];
}

interface Dimension {
  id: string;
  name: string;
  description: string;
  category: Category;
  displayOrder: number;
  components: Component[];
}

type Toast = { type: 'success' | 'error'; message: string } | null;

// Fixed 1..5 score / weight color scale
const SCORE_COLORS: Record<number, string> = {
  1: '#E53935',
  2: '#FB8C00',
  3: '#FDD835',
  4: '#43A047',
  5: '#2E7D32',
};

const scoreColor = (score: number): string => SCORE_COLORS[score] || '#9E9E9E';

let localIdCounter = 0;
const genLocalId = (prefix: string): string => `local-${prefix}-${Date.now()}-${localIdCounter++}`;

// ---------------------------------------------------------------------------
// Normalizers — coerce raw API payloads into strict local shapes
// ---------------------------------------------------------------------------

const normalizeAnswer = (raw: any, fallbackScore: number): Answer => ({
  id: raw?.id != null ? String(raw.id) : genLocalId('a'),
  text: typeof raw?.text === 'string' ? raw.text : '',
  score: Number(raw?.score) || fallbackScore,
});

const normalizeQuestion = (raw: any, componentId: string): Question => {
  const rawOptions: any[] = Array.isArray(raw?.options)
    ? raw.options
    : Array.isArray(raw?.answerOptions)
      ? raw.answerOptions
      : [];
  const options = rawOptions.map((o, i) => normalizeAnswer(o, i + 1));
  return {
    id: raw?.id != null ? String(raw.id) : genLocalId('q'),
    componentId: raw?.componentId != null ? String(raw.componentId) : componentId,
    text: typeof raw?.text === 'string' ? raw.text : '',
    options,
  };
};

const normalizeComponent = (raw: any, dimensionId: string): Component => ({
  id: raw?.id != null ? String(raw.id) : genLocalId('c'),
  dimensionId: raw?.dimensionId != null ? String(raw.dimensionId) : dimensionId,
  name: typeof raw?.name === 'string' ? raw.name : '',
  description: typeof raw?.description === 'string' ? raw.description : '',
  displayOrder: Number(raw?.displayOrder) || 0,
  weight: Number(raw?.weight) || 3,
  questions: Array.isArray(raw?.questions)
    ? raw.questions.map((q: any) => normalizeQuestion(q, raw?.id != null ? String(raw.id) : dimensionId))
    : [],
});

const normalizeDimension = (raw: any): Dimension => {
  const id = raw?.id != null ? String(raw.id) : genLocalId('d');
  return {
    id,
    name: typeof raw?.name === 'string' ? raw.name : '',
    description: typeof raw?.description === 'string' ? raw.description : '',
    category: effectiveAspect(raw?.category),
    displayOrder: Number(raw?.displayOrder) || 0,
    components: Array.isArray(raw?.components)
      ? raw.components.map((c: any) => normalizeComponent(c, id))
      : [],
  };
};

// ---------------------------------------------------------------------------
// Low-level API helper
// ---------------------------------------------------------------------------

const apiCall = async (url: string, method: string, body?: unknown): Promise<any> => {
  const res = await authFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const data = await res.json();
      msg = data?.error || data?.message || msg;
    } catch {
      // ignore parse failure
    }
    throw new Error(msg || `Request failed (${res.status})`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

// ---------------------------------------------------------------------------
// EditableText — dashed-underline input, auto-saves on blur (only if changed)
// ---------------------------------------------------------------------------

interface EditableTextProps {
  value: string;
  onSave: (next: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

const EditableText: React.FC<EditableTextProps> = ({ value, onSave, placeholder, className, ariaLabel }) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else if (!trimmed) {
      setDraft(value); // don't allow blanks
    }
  };

  return (
    <input
      type="text"
      className={`fb-edit ${className || ''}`}
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// WeightDots — five clickable dots; colors 1..weight
// ---------------------------------------------------------------------------

const WeightDots: React.FC<{ weight: number; onPick: (w: number) => void }> = ({ weight, onPick }) => (
  <div className="fb-weight-dots" role="group" aria-label="Component weight">
    {[1, 2, 3, 4, 5].map((w) => (
      <button
        key={w}
        type="button"
        className={`fb-dot ${w <= weight ? 'filled' : ''}`}
        data-weight={w}
        title={`Set weight ${w}`}
        aria-label={`Set weight ${w}`}
        style={w <= weight ? { backgroundColor: SCORE_COLORS[w], borderColor: SCORE_COLORS[w] } : undefined}
        onClick={() => onPick(w)}
      />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// AnswerLine — one answer per line (vertical stack)
// ---------------------------------------------------------------------------

interface AnswerLineProps {
  answer: Answer;
  onCycleScore: () => void;
  onEditText: (text: string) => void;
  onDelete: () => void;
}

const AnswerLine: React.FC<AnswerLineProps> = ({ answer, onCycleScore, onEditText, onDelete }) => (
  <div className="fb-answer-line">
    <button
      type="button"
      className="fb-score-badge"
      style={{ backgroundColor: scoreColor(answer.score) }}
      title="Click to change score"
      aria-label={`Score ${answer.score}, click to change`}
      onClick={onCycleScore}
    >
      {answer.score}
    </button>
    <EditableText
      value={answer.text}
      onSave={onEditText}
      placeholder="Answer text"
      className="fb-answer-text"
      ariaLabel="Answer text"
    />
    <button
      type="button"
      className="fb-icon-btn fb-answer-delete"
      title="Delete answer"
      aria-label="Delete answer"
      onClick={onDelete}
    >
      <i className="material-icons">close</i>
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FrameworkBuilder: React.FC = () => {
  const [tree, setTree] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'All' | Category>('All');
  /** The New Dimension dialog: null when closed. Its aspect is REQUIRED, so it starts unset. */
  const [newDimension, setNewDimension] = useState<{ name: string; category: Aspect | '' } | null>(
    null,
  );
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<Toast>(null);
  const [frameworkName, setFrameworkName] = useState<string>('APR sample framework');
  /**
   * Which framework this Builder shows.
   *
   * It used to be `?framework=` or a hard-coded fallback, with NO on-screen selector — so
   * the only way to build a different framework was to go back to the Frameworks list and
   * press Build again, and the screen gave no hint that it was showing one of several.
   * Owner-directed 2026-08-25: the framework is chosen here and switching is one click.
   *
   * The URL stays the source of truth so a link to a specific framework still works, and so
   * the browser's back button walks the frameworks you looked at.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const frameworkId = searchParams.get('framework');
  const activeFrameworkIdRef = useRef<string | null>(null);
  /** Every APR framework, for the selector. Loaded once with the tree. */
  const [frameworks, setFrameworks] = useState<{ id: string; name: string }[]>([]);
  /**
   * The framework actually being shown, in STATE as well as the ref.
   *
   * The ref alone cannot drive the selector: a ref does not re-render, so with no
   * `?framework=` in the URL the control would sit on a stale or blank value while the tree
   * below it showed the resolved default. The ref stays because the mutation handlers read it
   * synchronously, mid-callback, where state would be a render behind.
   */
  const [activeFrameworkId, setActiveFrameworkId] = useState<string>('');
  /**
   * `framework_dimensions.id` / `framework_components.id` for the framework on screen.
   *
   * Removing something FROM A FRAMEWORK deletes its mapping row, not the library row, and the
   * mapping id is the only handle on it. The library tree from `/api/dimensions` does not
   * carry these — they come from `GET /api/frameworks/:id`.
   */
  const [dimensionMapping, setDimensionMapping] = useState<Record<string, string>>({});
  const [componentMapping, setComponentMapping] = useState<Record<string, string>>({});

  // keep a ref of the tree for snapshot/rollback on failed optimistic updates
  const treeRef = useRef<Dimension[]>([]);
  useEffect(() => {
    treeRef.current = tree;
  }, [tree]);

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiCall('/api/dimensions', 'GET');
      let dims: Dimension[] = Array.isArray(data) ? data.map(normalizeDimension) : [];
      /*
       * NOTHING IS SELECTED BY DEFAULT — owner-directed.
       *
       * This used to fall back to the APR sample framework, or failing that the first row, so
       * the Builder always opened editing SOMETHING. With one framework that was harmless;
       * with several it means the first edit of a session lands on whichever framework
       * happened to sort first — and the selector agreed with it, so nothing looked wrong.
       *
       * A framework now comes from `?framework=` and nowhere else. "Build" on the Frameworks
       * list still opens straight into one, because it passes that parameter; arriving here
       * cold asks you to choose.
       */
      try {
        const fws = await apiCall('/api/frameworks', 'GET');
        if (Array.isArray(fws)) {
          setFrameworks(
            fws
              .filter((f: any) => f && f.id)
              .map((f: any) => ({ id: String(f.id), name: String(f.name ?? 'Untitled') })),
          );
        }
        const fw =
          frameworkId && Array.isArray(fws)
            ? fws.find((f: any) => String(f.id) === frameworkId)
            : null;

        setFrameworkName(fw && fw.name ? String(fw.name) : '');
        activeFrameworkIdRef.current = fw && fw.id ? String(fw.id) : null;
        setActiveFrameworkId(fw && fw.id ? String(fw.id) : '');

        if (fw && fw.id) {
          const detail = await apiCall(`/api/frameworks/${fw.id}`, 'GET');
          const allowed = new Set(
            (Array.isArray(detail && detail.dimensions) ? detail.dimensions : []).map((d: any) => d.id)
          );
          dims = allowed.size > 0 ? dims.filter((d) => allowed.has(d.id)) : [];

          /*
           * REGROUP THE COMPONENTS BY THIS FRAMEWORK'S PLACEMENT.
           *
           * `/api/dimensions` returns the LIBRARY tree — every component under its own
           * `dimension_id`. Since migration 0085 a framework decides where a component sits,
           * so a component moved in the Designer would otherwise still appear under its old
           * dimension here, and the two screens would disagree about the same framework.
           *
           * A component the framework has not placed is dropped rather than shown: it is not
           * part of this framework, and editing it here would edit it for the frameworks that
           * do use it.
           */
          const placement = new Map<string, string>();
          const compMap: Record<string, string> = {};
          for (const fc of Array.isArray(detail?.components) ? detail.components : []) {
            if (fc?.id && fc?.dimension_id) placement.set(String(fc.id), String(fc.dimension_id));
            if (fc?.id && fc?.mapping_id) compMap[String(fc.id)] = String(fc.mapping_id);
          }
          const dimMap: Record<string, string> = {};
          for (const fd of Array.isArray(detail?.dimensions) ? detail.dimensions : []) {
            if (fd?.id && fd?.mapping_id) dimMap[String(fd.id)] = String(fd.mapping_id);
          }
          setDimensionMapping(dimMap);
          setComponentMapping(compMap);
          if (placement.size > 0) {
            const pool = dims.flatMap((d) => d.components);
            dims = dims.map((d) => ({
              ...d,
              components: pool.filter((c) => placement.get(c.id) === d.id),
            }));
          }
        } else {
          // Nothing chosen: show no tree rather than the whole library, which would be every
          // dimension across every framework, editable without saying whose it is.
          dims = [];
        }
      } catch {
        /* keep whatever the library returned on any framework lookup error */
      }
      setTree(dims);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load framework');
    } finally {
      setLoading(false);
    }
  }, [frameworkId]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const notifyOk = (message = 'Saved') => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  // Run an optimistic mutation: apply `mutate` to state immediately, run the
  // server `persist`; on failure revert to the pre-mutation snapshot.
  const optimistic = async (
    mutate: (dims: Dimension[]) => Dimension[],
    persist: () => Promise<unknown>,
    okMessage?: string,
  ) => {
    const snapshot = treeRef.current;
    setTree((dims) => mutate(dims));
    try {
      await persist();
      notifyOk(okMessage);
    } catch (err) {
      setTree(snapshot);
      notifyErr(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // -- immutable tree updaters ------------------------------------------------

  const mapDimension = (dims: Dimension[], dimId: string, fn: (d: Dimension) => Dimension): Dimension[] =>
    dims.map((d) => (d.id === dimId ? fn(d) : d));

  const mapComponent = (
    dims: Dimension[],
    dimId: string,
    compId: string,
    fn: (c: Component) => Component,
  ): Dimension[] =>
    mapDimension(dims, dimId, (d) => ({
      ...d,
      components: d.components.map((c) => (c.id === compId ? fn(c) : c)),
    }));

  const mapQuestion = (
    dims: Dimension[],
    dimId: string,
    compId: string,
    questionId: string,
    fn: (q: Question) => Question,
  ): Dimension[] =>
    mapComponent(dims, dimId, compId, (c) => ({
      ...c,
      questions: c.questions.map((q) => (q.id === questionId ? fn(q) : q)),
    }));

  // -- Dimension actions ------------------------------------------------------

  /**
   * Owner-directed: a dimension must say who answers it AT CREATION.
   *
   * It used to appear instantly as "New Dimension" tagged Business. That is no longer a
   * harmless default — it decides who gets asked — so the name and the aspect are both
   * chosen before anything is written. The server refuses a create without a valid aspect,
   * so this is not the only line of defence.
   */
  const addDimension = async (name: string, category: Aspect) => {
    try {
      const created = await apiCall('/api/dimensions', 'POST', {
        name,
        description: '',
        category,
      });
      const dim = normalizeDimension(created || { name, category });
      // When editing a specific framework, link the new dimension to it so it
      // isn't filtered out of the view.
      const fwId = activeFrameworkIdRef.current;
      if (fwId && dim.id) {
        try {
          await apiCall(`/api/frameworks/${fwId}/dimensions`, 'POST', {
            dimension_id: dim.id,
            display_order: 0,
            is_mandatory: true,
          });
        } catch {
          /* dimension still created globally; ignore link failure */
        }
      }
      setTree((dims) => [...dims, dim]);
      setNewDimension(null);
      notifyOk('Dimension added');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to add dimension');
    }
  };

  const renameDimension = (dimId: string, name: string) =>
    optimistic(
      (dims) => mapDimension(dims, dimId, (d) => ({ ...d, name })),
      () => apiCall(`/api/dimensions/${dimId}`, 'PUT', { name }),
    );

  /**
   * Five values cannot be a toggle, so the chip became a selector. Same single-field PUT the
   * toggle used — the server's update is partial, so this saves the aspect on its own.
   */
  const setAspect = (dim: Dimension, next: Aspect) => {
    if (next === dim.category) return;
    return optimistic(
      (dims) => mapDimension(dims, dim.id, (d) => ({ ...d, category: next })),
      () => apiCall(`/api/dimensions/${dim.id}`, 'PUT', { category: next }),
    );
  };

  /**
   * REMOVE FROM THIS FRAMEWORK — not delete from the library.
   *
   * This used to call `DELETE /api/dimensions/:id`, which removes the dimension itself and
   * cascades to its components, questions and every answer option — in EVERY framework that
   * used it. The Builder shows one framework at a time, so there was nothing on screen to say
   * that three other frameworks had just lost the same content. It is how "Strategy Only"
   * emptied APR sample framework.
   *
   * Now it deletes the `framework_dimensions` mapping. The dimension and all its content stay
   * in the library and in every other framework; only this framework stops using it. The
   * `framework_components` rows for it go too, by cascade, which is correct — a placement in
   * a framework that no longer has the dimension is meaningless.
   */
  const deleteDimension = (dim: Dimension) => {
    const fwId = activeFrameworkIdRef.current;
    const mappingId = dimensionMapping[dim.id];
    if (!fwId || !mappingId) {
      notifyErr('Select a framework before removing a dimension from it');
      return;
    }
    if (
      !window.confirm(
        `Remove "${dim.name}" from ${frameworkName || 'this framework'}?\n\n` +
          'It stays in the library, with its components, questions and answers, and any other ' +
          'framework using it is unaffected.',
      )
    ) {
      return;
    }
    optimistic(
      (dims) => dims.filter((d) => d.id !== dim.id),
      () => apiCall(`/api/frameworks/${fwId}/dimensions/${mappingId}`, 'DELETE'),
      'Removed from this framework',
    );
  };

  // -- Component actions ------------------------------------------------------

  const addComponent = async (dimId: string) => {
    try {
      const created = await apiCall('/api/components', 'POST', {
        name: 'New Component',
        description: '',
        dimensionId: dimId,
        weight: 3,
      });
      const comp = normalizeComponent(created || { name: 'New Component', weight: 3 }, dimId);
      comp.dimensionId = dimId;

      /*
       * Place it in the framework being edited. Creating a component gives it a home in the
       * LIBRARY; since 0085 that is not the same as being in this framework, and a component
       * that was added here and then vanished on reload would be the obvious bug.
       */
      const fwId = activeFrameworkIdRef.current;
      if (fwId && comp.id) {
        try {
          await apiCall(`/api/frameworks/${fwId}/components/reorder-layout`, 'POST', {
            items: [{ id: comp.id, dimensionId: dimId, displayOrder: 0 }],
          });
        } catch {
          /* the component exists in the library; a failed placement is not a failed create */
        }
      }

      setTree((dims) => mapDimension(dims, dimId, (d) => ({ ...d, components: [...d.components, comp] })));
      notifyOk('Component added');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to add component');
    }
  };

  const renameComponent = (dimId: string, compId: string, name: string) =>
    optimistic(
      (dims) => mapComponent(dims, dimId, compId, (c) => ({ ...c, name })),
      () => apiCall(`/api/components/${compId}`, 'PUT', { name }),
    );

  const setComponentWeight = (dimId: string, comp: Component, weight: number) => {
    if (comp.weight === weight) return;
    optimistic(
      (dims) => mapComponent(dims, dimId, comp.id, (c) => ({ ...c, weight })),
      () => apiCall(`/api/components/${comp.id}/weight`, 'PUT', { weight }),
    );
  };

  /**
   * REMOVE FROM THIS FRAMEWORK — same reasoning as the dimension above.
   *
   * `DELETE /api/components/:id` destroys the component with its questions and answer options
   * everywhere it is used. Since placement became per-framework (migration 0085), a component
   * can legitimately belong to several frameworks under different dimensions, so removing it
   * here deletes this framework's `framework_components` row and nothing else.
   */
  const deleteComponent = (dimId: string, comp: Component) => {
    const fwId = activeFrameworkIdRef.current;
    const mappingId = componentMapping[comp.id];
    if (!fwId || !mappingId) {
      notifyErr('Select a framework before removing a component from it');
      return;
    }
    if (
      !window.confirm(
        `Remove "${comp.name}" from ${frameworkName || 'this framework'}?\n\n` +
          'It stays in the library with its questions and answers, and any other framework ' +
          'using it is unaffected.',
      )
    ) {
      return;
    }
    optimistic(
      (dims) => mapDimension(dims, dimId, (d) => ({ ...d, components: d.components.filter((c) => c.id !== comp.id) })),
      () => apiCall(`/api/frameworks/${fwId}/components/${mappingId}`, 'DELETE'),
      'Removed from this framework',
    );
  };

  // -- Question actions -------------------------------------------------------

  const addQuestion = async (dimId: string, compId: string) => {
    try {
      const created = await apiCall('/api/questions', 'POST', {
        text: 'New question',
        componentId: compId,
      });
      const question = normalizeQuestion(created || { text: 'New question' }, compId);
      question.componentId = compId;
      // Server auto-creates 5 default answers; if the payload omitted them, synthesize.
      if (question.options.length === 0) {
        question.options = [1, 2, 3, 4, 5].map((s) => ({ id: genLocalId('a'), text: `Answer ${s}`, score: s }));
      }
      setTree((dims) => mapComponent(dims, dimId, compId, (c) => ({ ...c, questions: [...c.questions, question] })));
      notifyOk('Question added');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to add question');
    }
  };

  const renameQuestion = (dimId: string, compId: string, questionId: string, text: string) =>
    optimistic(
      (dims) => mapQuestion(dims, dimId, compId, questionId, (q) => ({ ...q, text })),
      () => apiCall(`/api/questions/${questionId}`, 'PUT', { text }),
    );

  const deleteQuestion = (dimId: string, compId: string, question: Question) => {
    if (!window.confirm('Delete this question?')) return;
    optimistic(
      (dims) => mapComponent(dims, dimId, compId, (c) => ({ ...c, questions: c.questions.filter((q) => q.id !== question.id) })),
      () => apiCall(`/api/questions/${question.id}`, 'DELETE'),
      'Question deleted',
    );
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      next.delete(question.id);
      return next;
    });
  };

  const toggleQuestionExpand = (questionId: string) =>
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });

  // -- Answer actions (all persisted via PUT /api/questions/:id {answerOptions}) --

  // Persist the FULL answer set for a question. `nextOptions` must already have
  // unique scores in 1..5. Optimistically apply then save; revert on failure.
  const persistAnswers = (dimId: string, compId: string, question: Question, nextOptions: Answer[], okMessage?: string) =>
    optimistic(
      (dims) => mapQuestion(dims, dimId, compId, question.id, (q) => ({ ...q, options: nextOptions })),
      () =>
        apiCall(`/api/questions/${question.id}`, 'PUT', {
          answerOptions: nextOptions.map((o) => ({ text: o.text, score: o.score })),
        }),
      okMessage,
    );

  const lowestFreeScore = (options: Answer[]): number => {
    const used = new Set(options.map((o) => o.score));
    for (let s = 1; s <= 5; s++) {
      if (!used.has(s)) return s;
    }
    return 0; // no free score (question already has 5 answers)
  };

  const addAnswer = (dimId: string, compId: string, question: Question) => {
    if (question.options.length >= 5) return;
    const score = lowestFreeScore(question.options);
    if (score === 0) return;
    const next = [...question.options, { id: genLocalId('a'), text: 'New answer', score }];
    persistAnswers(dimId, compId, question, next, 'Answer added');
  };

  const deleteAnswer = (dimId: string, compId: string, question: Question, answerId: string) => {
    const next = question.options.filter((o) => o.id !== answerId);
    persistAnswers(dimId, compId, question, next, 'Answer removed');
  };

  const editAnswerText = (dimId: string, compId: string, question: Question, answerId: string, text: string) => {
    const next = question.options.map((o) => (o.id === answerId ? { ...o, text } : o));
    persistAnswers(dimId, compId, question, next);
  };

  // Cycle score to the next UNUSED score among 1..5 (skip sibling scores).
  const cycleScore = (dimId: string, compId: string, question: Question, answer: Answer) => {
    const used = new Set(question.options.filter((o) => o.id !== answer.id).map((o) => o.score));
    let nextScore = answer.score;
    for (let step = 1; step <= 5; step++) {
      const cand = ((answer.score - 1 + step) % 5) + 1;
      if (cand === answer.score) break;
      if (!used.has(cand)) {
        nextScore = cand;
        break;
      }
    }
    if (nextScore === answer.score) return; // no free score to move to
    const next = question.options.map((o) => (o.id === answer.id ? { ...o, score: nextScore } : o));
    persistAnswers(dimId, compId, question, next);
  };

  // -- Derived counts (whole library, not filtered) ---------------------------

  const dimCount = tree.length;
  const compCount = tree.reduce((n, d) => n + d.components.length, 0);
  const questionCount = tree.reduce((n, d) => n + d.components.reduce((m, c) => m + c.questions.length, 0), 0);

  const visibleDims = tree.filter((d) => categoryFilter === 'All' || d.category === categoryFilter);

  // -- Row flattening ---------------------------------------------------------

  interface Row {
    key: string;
    dimension: Dimension;
    component: Component | null;
    question: Question | null;
    isFirstOfDimension: boolean;
    isFirstOfComponent: boolean;
  }

  const rows: Row[] = [];
  for (const dim of visibleDims) {
    let dimEmitted = false;
    if (dim.components.length === 0) {
      rows.push({
        key: `dim-empty-${dim.id}`,
        dimension: dim,
        component: null,
        question: null,
        isFirstOfDimension: true,
        isFirstOfComponent: false,
      });
      continue;
    }
    for (const comp of dim.components) {
      if (comp.questions.length === 0) {
        rows.push({
          key: `comp-empty-${comp.id}`,
          dimension: dim,
          component: comp,
          question: null,
          isFirstOfDimension: !dimEmitted,
          isFirstOfComponent: true,
        });
        dimEmitted = true;
      } else {
        comp.questions.forEach((q, qi) => {
          rows.push({
            key: `q-${q.id}`,
            dimension: dim,
            component: comp,
            question: q,
            isFirstOfDimension: !dimEmitted,
            isFirstOfComponent: qi === 0,
          });
          dimEmitted = true;
        });
      }
    }
  }

  const subtitle = `${dimCount} dimension${dimCount === 1 ? '' : 's'} · ${compCount} component${compCount === 1 ? '' : 's'} · ${questionCount} question${questionCount === 1 ? '' : 's'}`;

  // -- Render -----------------------------------------------------------------

  return (
    <div className="page fb-page">
      <HelpPanel help={FrameworkBuilderHelp} />
      <PageHeader
        title="Framework Builder"
        subtitle={subtitle}
      />

      {/*
        THE CONTROLS SIT ON THEIR OWN ROW, BELOW THE HEADING — owner-directed.

        They were in PageHeader's `actions` slot, which `.rd-page-header-row` lays out with
        `justify-content: space-between` — so they rode on the same line as the title, pushed
        hard right. Three controls of different widths reading right-to-left across from a
        heading is not a toolbar. On their own row they read left-to-right in the order you
        use them: which framework, then what to show, then add.

        PageHeader itself is untouched. It is shared by every APR screen, and changing where
        it puts actions would move them on all of them.
      */}
      <div className="fb-toolbar">
          {/*
            WHICH FRAMEWORK — owner-directed. Writing the choice into the URL rather than
            into state keeps `?framework=` the single source of truth, so the load effect
            that already keys on it does the reload, and a shared link still opens the same
            framework.
          */}
          <label className="fb-filter">
            <span>Framework:</span>
            <select
              className="fb-framework-select"
              value={activeFrameworkId}
              onChange={(e) =>
                e.target.value
                  ? setSearchParams({ framework: e.target.value })
                  : setSearchParams({})
              }
              aria-label="Choose a framework to build"
            >
              {/* The empty option is a real choice, not a prompt to skip past: picking it
                  clears `?framework=` and puts the screen back to asking. */}
              <option value="">— Select Framework —</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label className="fb-filter">
            <span>Show:</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as 'All' | Category)}
              aria-label="Filter by responsible aspect"
            >
              <option value="All">All</option>
              {ASPECTS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setNewDimension({ name: '', category: '' })}
          >
            + Add Dimension
          </button>
      </div>

      <div className="fb-framework-ribbon">
        <span className="material-icons" aria-hidden="true">category</span>
        <span className="fb-framework-ribbon-label">Framework</span>
        <span className="fb-framework-ribbon-name">{frameworkName}</span>
      </div>

      <div className="fb-container">
        {loading ? (
          <div className="fb-state">Loading framework…</div>
        ) : error ? (
          <div className="fb-state fb-state-error">
            <p>⚠️ {error}</p>
            <button type="button" className="btn btn-primary" onClick={loadTree}>
              Retry
            </button>
          </div>
        ) : !activeFrameworkId ? (
          /*
            Nothing selected. Distinct from "this framework has no dimensions": offering
            + Add Dimension here would build into no framework at all, and the dimension
            would land in the library attached to nothing.
          */
          <div className="fb-state">
            <p>Choose a framework above to build it.</p>
            <p style={{ fontSize: 13, color: '#5f6368', marginTop: 4 }}>
              Its dimensions, components, questions and answers appear here. Each dimension
              says who answers it, and only that owner is asked those questions.
            </p>
          </div>
        ) : tree.length === 0 ? (
          <div className="fb-state">
            <p>No dimensions yet in {frameworkName || 'this framework'}.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setNewDimension({ name: '', category: '' })}
            >
              + Add Dimension
            </button>
          </div>
        ) : (
          <div className="fb-table-wrap">
            <table className="fb-table">
              <colgroup>
                <col className="fb-col-dim" />
                <col className="fb-col-comp" />
                <col className="fb-col-q" />
                <col className="fb-col-weight" />
              </colgroup>
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th>Component</th>
                  <th>Question</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const { dimension, component, question } = row;
                  const isExpanded = question ? expandedQuestions.has(question.id) : false;
                  return (
                    <React.Fragment key={row.key}>
                      <tr className={row.isFirstOfDimension ? 'fb-dim-start' : ''}>
                        {/* DIMENSION cell */}
                        <td className="fb-cell-dim">
                          {row.isFirstOfDimension && (
                            <div className="fb-dim-block">
                              <div className="fb-dim-row">
                                <EditableText
                                  value={dimension.name}
                                  onSave={(v) => renameDimension(dimension.id, v)}
                                  className="fb-dim-name"
                                  placeholder="Dimension name"
                                  ariaLabel="Dimension name"
                                />
                                <button
                                  type="button"
                                  className="fb-icon-btn fb-danger fb-danger-glyph"
                                  title="Remove this dimension from this framework"
                                  aria-label="Remove dimension from this framework"
                                  onClick={() => deleteDimension(dimension)}
                                >
                                  {/* Application Master Data's delete glyph, owner-directed:
                                      one delete control across the product rather than a
                                      Material ligature here and 🗑️ there. */}
                                  🗑️
                                </button>
                              </div>
                              <div className="fb-dim-row">
                                <select
                                  className="fb-aspect-select"
                                  style={{
                                    color: ASPECT_COLOR[dimension.category].fg,
                                    backgroundColor: ASPECT_COLOR[dimension.category].bg,
                                  }}
                                  value={dimension.category}
                                  onChange={(e) => setAspect(dimension, e.target.value as Aspect)}
                                  title={`${ASPECT_OWNER[dimension.category]} — ${ASPECT_HINT[dimension.category]}`}
                                  aria-label={`Who answers ${dimension.name}`}
                                >
                                  {ASPECTS.map((a) => (
                                    <option key={a} value={a}>
                                      {a}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className="fb-link"
                                onClick={() => addComponent(dimension.id)}
                              >
                                + component
                              </button>
                            </div>
                          )}
                        </td>

                        {/* COMPONENT cell */}
                        <td className="fb-cell-comp">
                          {row.isFirstOfComponent && component && (
                            <div className="fb-comp-block">
                              <div className="fb-comp-row">
                                <EditableText
                                  value={component.name}
                                  onSave={(v) => renameComponent(dimension.id, component.id, v)}
                                  className="fb-comp-name"
                                  placeholder="Component name"
                                  ariaLabel="Component name"
                                />
                                <button
                                  type="button"
                                  className="fb-icon-btn fb-danger fb-danger-glyph"
                                  title="Remove this component from this framework"
                                  aria-label="Remove component from this framework"
                                  onClick={() => deleteComponent(dimension.id, component)}
                                >
                                  🗑️
                                </button>
                              </div>
                              <button
                                type="button"
                                className="fb-link"
                                onClick={() => addQuestion(dimension.id, component.id)}
                              >
                                + question
                              </button>
                            </div>
                          )}
                        </td>

                        {/* QUESTION cell */}
                        <td className="fb-cell-q">
                          {question && component && (
                            <div className="fb-q-row">
                              <EditableText
                                value={question.text}
                                onSave={(v) => renameQuestion(dimension.id, component.id, question.id, v)}
                                className="fb-q-text"
                                placeholder="Question text"
                                ariaLabel="Question text"
                              />
                              <button
                                type="button"
                                className={`fb-qa-toggle ${isExpanded ? 'open' : ''}`}
                                onClick={() => toggleQuestionExpand(question.id)}
                                title="Show answers"
                                aria-expanded={isExpanded}
                              >
                                Q&amp;A {isExpanded ? '▴' : '▾'}
                              </button>
                              <button
                                type="button"
                                className="fb-icon-btn fb-danger fb-danger-glyph"
                                title="Delete question"
                                aria-label="Delete question"
                                onClick={() => deleteQuestion(dimension.id, component.id, question)}
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </td>

                        {/* WEIGHT cell */}
                        <td className="fb-cell-weight">
                          {row.isFirstOfComponent && component && (
                            <WeightDots
                              weight={component.weight}
                              onPick={(w) => setComponentWeight(dimension.id, component, w)}
                            />
                          )}
                        </td>
                      </tr>

                      {question && component && isExpanded && (
                        <tr className="fb-answers-row">
                          <td />
                          <td />
                          <td colSpan={2}>
                            <div className="fb-answers-panel">
                              {question.options.length === 0 ? (
                                <p className="fb-answers-empty">No answers yet.</p>
                              ) : (
                                <div className="fb-answers-list">
                                  {[...question.options]
                                    .sort((a, b) => a.score - b.score)
                                    .map((ans) => (
                                      <AnswerLine
                                        key={ans.id}
                                        answer={ans}
                                        onCycleScore={() => cycleScore(dimension.id, component.id, question, ans)}
                                        onEditText={(t) => editAnswerText(dimension.id, component.id, question, ans.id, t)}
                                        onDelete={() => deleteAnswer(dimension.id, component.id, question, ans.id)}
                                      />
                                    ))}
                                </div>
                              )}
                              <button
                                type="button"
                                className="fb-link fb-add-answer"
                                disabled={question.options.length >= 5}
                                onClick={() => addAnswer(dimension.id, component.id, question)}
                              >
                                + Add answer
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/*
        New Dimension. Owner-directed: the responsible aspect is chosen HERE, because it
        decides who gets asked the questions underneath it and there is no safe default for
        that. `form-modal` is APR's own dialog shell (source-css/app.css:3321), already scoped
        under `.apr-app`, so no new chrome was written.
      */}
      {newDimension && (
        <div
          className="form-modal"
          onClick={(e) => e.target === e.currentTarget && setNewDimension(null)}
        >
          <div className="form-modal-content">
            <div className="form-modal-header">
              <h2>New Dimension</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setNewDimension(null)}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            <form
              className="customer-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newDimension.name.trim() || !newDimension.category) return;
                void addDimension(newDimension.name.trim(), newDimension.category);
              }}
            >
              <div className="form-group">
                <label htmlFor="fb-new-dim-name">Name *</label>
                <input
                  id="fb-new-dim-name"
                  type="text"
                  value={newDimension.name}
                  onChange={(e) => setNewDimension({ ...newDimension, name: e.target.value })}
                  required
                  autoFocus
                  placeholder="e.g., Security Posture"
                />
              </div>
              <div className="form-group">
                <label htmlFor="fb-new-dim-aspect">Who answers this dimension? *</label>
                <select
                  id="fb-new-dim-aspect"
                  value={newDimension.category}
                  onChange={(e) =>
                    setNewDimension({ ...newDimension, category: e.target.value as Aspect })
                  }
                  required
                >
                  <option value="">— Select —</option>
                  {ASPECTS.map((a) => (
                    <option key={a} value={a}>
                      {a} — {ASPECT_OWNER[a]}
                    </option>
                  ))}
                </select>
                <p className="fb-field-hint">
                  {newDimension.category
                    ? ASPECT_HINT[newDimension.category]
                    : 'Every question in this dimension goes to this owner on each application.'}
                </p>
              </div>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setNewDimension(null)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!newDimension.name.trim() || !newDimension.category}
                >
                  Add Dimension
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fb-toast fb-toast-${toast.type}`} role="status">
          {toast.type === 'success' ? '✓ ' : '⚠ '}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default FrameworkBuilder;
